import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { streamMediaURL } from '../utility/ReUsableFunctions';
import './LeaderboardWidget.css';

const LeaderboardWidget = ({ leaderboardData = [], userRankInfo = null, currentUserProfile = null, loading = false, hideEmails = false }) => {
  const [avatarError, setAvatarError] = useState(false);
  const [userAvatarErrors, setUserAvatarErrors] = useState({});
  
  // Get current user data for role checking
  const userData = useSelector((state) => state.users.userData);

  // Mock current user ID (replace with real user ID from auth context)
  const currentUserId = 1;

  // Check if current user can see emails (admin or community manager)
  const canSeeEmails = () => {
    return userData?.roles?.includes('TKL SYSTEM ADMIN') || 
           userData?.roles?.includes('TKL Community Manager');
  };



  // Helper function to get profile image URL
  const getProfileImageUrl = (user) => {
    if (user.profile_avatar_url) {
      return user.profile_avatar_url;
    }
    if (user.profile_avatar_slug) {
      return streamMediaURL(user.profile_avatar_slug);
    }
    return null;
  };

  // Handle avatar error for specific user
  const handleUserAvatarError = (userId) => {
    setUserAvatarErrors(prev => ({
      ...prev,
      [userId]: true
    }));
  };

  // Find the current user's row
  const currentUser = leaderboardData.find(user => user.user_id === currentUserId);
  const top10 = leaderboardData.slice(0, 10);

  // Check if current user is in top 10
  const isCurrentUserInTop10 = currentUserProfile && top10.some(
    user => user.user_id === userRankInfo?.user_id
  );

  return (
    <div className="tkl-leaderboard-widget">
      <div className="tkl-leaderboard-section">
        <div className="tkl-leaderboard-header" style={{ display: "flex", justifyContent: "space-between" }}>
          <span>User</span>
          <span>Rank</span>
        </div>
        <div>
          {loading ? (
            <div>Loading...</div>
          ) : leaderboardData.length === 0 ? (
            <div>No leaderboard data available.</div>
          ) : (
            <>
              {top10.map((user, idx) => {
                // Check if this is the current user's row
                const isCurrentUser = userRankInfo && user.user_id === userRankInfo.user_id;
                
                return (
                <div
                  key={idx}
                  className="tkl-leaderboard-row"
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: isCurrentUser ? "12px 16px" : "8px 0",
                    borderBottom: isCurrentUser ? "none" : "1px solid #ececec",
                    background: isCurrentUser 
                      ? 'linear-gradient(135deg, #FFF8E6 0%, #FCF4D6 100%)' 
                      : 'transparent',
                    borderRadius: isCurrentUser ? 12 : 0,
                    border: isCurrentUser ? '2px solid #FCB227' : 'none',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  {/* Subtle background pattern for current user */}
                  {isCurrentUser && (
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      width: '100px',
                      height: '100%',
                      background: 'linear-gradient(45deg, rgba(252, 178, 39, 0.05) 0%, rgba(252, 178, 39, 0.02) 100%)',
                      borderRadius: '50% 0 0 50%'
                    }} />
                  )}
                  <div style={{ 
                    display: "flex", 
                    alignItems: "center",
                    flex: 1,
                    minWidth: 0,
                    marginRight: 16
                  }}>
                    {!userAvatarErrors[user.user_id] && getProfileImageUrl(user) ? (
                      <img
                        src={getProfileImageUrl(user)}
                        alt={`${user.firstName || user.user_name} ${user.lastName || user.user_last_name}`}
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: "50%",
                          marginRight: 12,
                          objectFit: "cover",
                          backgroundColor: '#f0f0f0',
                          flexShrink: 0
                        }}
                        onError={() => handleUserAvatarError(user.user_id)}
                      />
                    ) : (
                      <div
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: "50%",
                          marginRight: 12,
                          backgroundColor: '#f0f0f0',
                          color: '#999',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: '1px solid #ddd',
                          flexShrink: 0
                        }}
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          style={{ color: '#999' }}
                        >
                          <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                        </svg>
                      </div>
                    )}
                    <div className="tkl-user-info-container" style={{ 
                      position: 'relative',
                      zIndex: 1,
                      flex: 1,
                      minWidth: 0,
                      overflow: 'hidden'
                    }}>
                      {user.user_slug ? (
                        <Link 
                          to={`/OtherProfile/${user.user_slug}`}
                          className="tkl-user-name"
                          style={{ 
                            textDecoration: 'none',
                            cursor: 'pointer',
                            display: 'block',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}
                          title={`${user.user_name} ${user.user_last_name}`}
                        >
                          {user.user_name} {user.user_last_name}
                        </Link>
                      ) : (
                        <span className="tkl-user-name" style={{
                          display: 'block',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                        title={`${user.user_name} ${user.user_last_name}`}>
                          {user.user_name} {user.user_last_name}
                        </span>
                      )}
                      {user.user_email && canSeeEmails() && !hideEmails && (
                        <span className="tkl-user-email" style={{
                          display: 'block',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          fontSize: '12px',
                          color: '#666'
                        }}>
                          {user.user_email}
                        </span>
                      )}
                    </div>
                  </div>
                  <span style={{ 
                    fontWeight: 600,
                    color: user.rank <= 3 ? '#FFD700' : (isCurrentUser ? '#FCB227' : '#222'),
                    fontSize: user.rank <= 3 ? '1.6rem' : '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '40px',
                    height: '32px',
                    textAlign: 'center',
                    lineHeight: 1,
                    position: 'relative',
                    zIndex: 1,
                    flexShrink: 0
                  }}>
                    {user.rank === 1 ? '🥇' : user.rank === 2 ? '🥈' : user.rank === 3 ? '🥉' : user.rank}
                  </span>
                </div>
                );
              })}
              {/* Current user row: better design when not in top 10 */}
              {!isCurrentUserInTop10 && userRankInfo && currentUserProfile && (
                <>
                  {/* Divider */}
                  <div style={{
                    margin: '16px 0 12px 0',
                    borderTop: '2px solid #FCF4D6',
                    position: 'relative'
                  }}>
                    <div style={{
                      position: 'absolute',
                      top: '-10px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      background: '#fff',
                      padding: '0 16px',
                      fontSize: '16px',
                      fontWeight: 700,
                      color: '#FCB227',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Your Rank
                    </div>
                  </div>
                  
                  <div
                    className="tkl-leaderboard-row"
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "12px 16px",
                      background: 'linear-gradient(135deg, #FFF8E6 0%, #FCF4D6 100%)',
                      borderRadius: 12,
                      border: '2px solid #FCB227',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                  >
                    {/* Subtle background pattern */}
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      width: '100px',
                      height: '100%',
                      background: 'linear-gradient(45deg, rgba(252, 178, 39, 0.05) 0%, rgba(252, 178, 39, 0.02) 100%)',
                      borderRadius: '50% 0 0 50%'
                    }} />
                    
                    <div style={{ display: "flex", alignItems: "center", flex: 1, minWidth: 0, position: 'relative', zIndex: 1 }}>
                      <div style={{ width: 44, flexShrink: 0, marginRight: 12 }}>
                        {!avatarError && userRankInfo.profile_avatar_url ? (
                          <img
                            src={userRankInfo.profile_avatar_url}
                            alt={`${currentUserProfile.firstName || ''} ${currentUserProfile.lastName || ''}`}
                            style={{
                              width: 40,
                              height: 40,
                              borderRadius: "50%",
                              objectFit: "cover",
                              border: '3px solid #FCB227',
                              backgroundColor: '#f0f0f0'
                            }}
                            onError={() => setAvatarError(true)}
                          />
                        ) : (
                          <div
                            style={{
                              width: 40,
                              height: 40,
                              borderRadius: "50%",
                              background: "#f0f0f0",
                              color: "#999",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              border: '3px solid #FCB227'
                            }}
                          >
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="currentColor"
                              style={{ color: '#999' }}
                            >
                              <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                            </svg>
                          </div>
                        )}
                      </div>
                      <div style={{ 
                        fontSize: '16px', 
                        fontWeight: 700,
                        color: '#000',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 4
                      }}>
                        <span>
                          {(currentUserProfile.firstName || currentUserProfile.lastName)
                            ? `${currentUserProfile.firstName} ${currentUserProfile.lastName}`
                            : "No Name Found"}
                        </span>
                        {currentUserProfile.email && canSeeEmails() && !hideEmails && (
                          <span className="tkl-user-email" style={{
                            fontSize: '12px'
                          }}>
                            {currentUserProfile.email}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      position: 'relative',
                      zIndex: 1
                    }}>
                      <div style={{
                        background: 'linear-gradient(135deg, #FCB227 0%, #FCA311 100%)',
                        color: 'white',
                        borderRadius: '20px',
                        padding: '8px 16px',
                        fontWeight: 700,
                        fontSize: '18px',
                        minWidth: '60px',
                        textAlign: 'center',
                        boxShadow: '0 4px 12px rgba(252, 178, 39, 0.3)',
                        border: '2px solid rgba(255, 255, 255, 0.2)'
                      }}>
                        #{userRankInfo.rank}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeaderboardWidget; 